import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { CheckCircle, CalendarCheck } from "@phosphor-icons/react";
import { toast } from "sonner";
import { demoRequest } from "@/lib/api";

const SIZES = ["Just me", "2–10", "11–50", "51–200", "200+"];
const INDUSTRIES = ["Restaurant / Food", "Café / Bakery", "Salon / Beauty", "Fitness / Wellness", "E-commerce / Retail",
  "Home Services / Trades", "Real Estate", "Professional Services", "Healthcare", "Automotive", "Travel / Hospitality", "Other"];

const F = ({ label, children, required }) => (
  <div>
    <Label className="label-caps">{label}{required && <span className="brand-text ml-0.5">*</span>}</Label>
    <div className="mt-1">{children}</div>
  </div>
);

export default function DemoRequestDialog({ open, onClose, source = "landing_page" }) {
  const [form, setForm] = useState({ name: "", business: "", email: "", phone: "", website: "", company_size: "", industry: "", current_tools: "", biggest_challenge: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const inputCls = "bg-[#10101A] border-white/10 h-10";

  const submit = async () => {
    if (!form.name.trim() || !form.business.trim() || !form.email.trim()) {
      toast.error("Name, business and email are required"); return;
    }
    setSending(true);
    try {
      await demoRequest({ ...form, source });
      setDone(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send your request — please try again");
    } finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#101018] border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto" data-testid="demo-request-dialog">
        {done ? (
          <div className="py-8 text-center" data-testid="demo-request-success">
            <CheckCircle size={52} className="text-emerald-400 mx-auto mb-4" weight="fill" />
            <h3 className="font-head font-bold text-xl">Demo request received!</h3>
            <p className="mt-2 text-sm text-[#9a9a9a] max-w-sm mx-auto">Check your inbox for a confirmation — a real human from our team will reach out within one business day to schedule your personalized demo.</p>
            <Button data-testid="demo-request-done-close" onClick={onClose} className="mt-6 bg-[#6B3DFF] hover:bg-[#A56CFF] text-white rounded-full px-6 h-10">Done</Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-head flex items-center gap-2"><CalendarCheck size={20} className="brand-text" weight="duotone" /> Book your personalized demo</DialogTitle>
              <DialogDescription className="text-[#9a9a9a] text-sm">Tell us a little about your business — we'll tailor the demo to exactly what you need. Takes 60 seconds.</DialogDescription>
            </DialogHeader>
            <div className="grid sm:grid-cols-2 gap-3.5 mt-1">
              <F label="Your name" required><Input data-testid="demo-name" value={form.name} onChange={set("name")} placeholder="Alex Rivera" className={inputCls} /></F>
              <F label="Business name" required><Input data-testid="demo-business" value={form.business} onChange={set("business")} placeholder="Rivera Roofing Co." className={inputCls} /></F>
              <F label="Email" required><Input data-testid="demo-email" type="email" value={form.email} onChange={set("email")} placeholder="alex@business.com" className={inputCls} /></F>
              <F label="Phone"><Input data-testid="demo-phone" value={form.phone} onChange={set("phone")} placeholder="+1 555 000 0000" className={inputCls} /></F>
              <F label="Website"><Input data-testid="demo-website" value={form.website} onChange={set("website")} placeholder="https://yourbusiness.com" className={inputCls} /></F>
              <F label="Company size">
                <select data-testid="demo-company-size" value={form.company_size} onChange={set("company_size")} className="w-full h-10 rounded-lg bg-[#10101A] border border-white/10 px-3 text-sm">
                  <option value="" className="bg-[#161622]">Select…</option>
                  {SIZES.map((s) => <option key={s} value={s} className="bg-[#161622]">{s}</option>)}
                </select>
              </F>
              <F label="Industry">
                <select data-testid="demo-industry" value={form.industry} onChange={set("industry")} className="w-full h-10 rounded-lg bg-[#10101A] border border-white/10 px-3 text-sm">
                  <option value="" className="bg-[#161622]">Select…</option>
                  {INDUSTRIES.map((s) => <option key={s} value={s} className="bg-[#161622]">{s}</option>)}
                </select>
              </F>
              <F label="Current marketing tools"><Input data-testid="demo-current-tools" value={form.current_tools} onChange={set("current_tools")} placeholder="e.g. Canva, Mailchimp, none" className={inputCls} /></F>
            </div>
            <F label="Your biggest marketing challenge">
              <Textarea data-testid="demo-biggest-challenge" value={form.biggest_challenge} onChange={set("biggest_challenge")} rows={2}
                placeholder="e.g. No time to post consistently and our emails never get sent…" className="bg-[#10101A] border-white/10" />
            </F>
            <Button data-testid="demo-request-submit" onClick={submit} disabled={sending}
              className="w-full bg-[#6B3DFF] hover:bg-[#A56CFF] text-white rounded-full h-11 mt-1">
              {sending ? "Sending…" : "Request My Demo"}
            </Button>
            <p className="text-[10px] text-[#5a5a5a] text-center">No spam, ever. We'll only use this to schedule and prepare your demo.</p>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
